import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import TableFilterDynamique, { FilterField } from "components/Common/TableFilter/TableFilterDynamique";
// import LeaveTable from "modules/leaves/components/leaveTable";
// import useLeave from "modules/leaves/hooks/useLeave";
import useReference from "hooks/useReference";
import Loader from "components/Common/loader";
import useTeacher from "../hooks/useTeacher";
import useLeave from "feature-module/hrm/hooks/useLeave";
import LeaveTable from "feature-module/hrm/components/leaveTable";
import { useOutletContext } from "react-router-dom";
import CustomAlert from "components/Common/ui/CustomAlert";
import RejectedLeaveModel from "feature-module/hrm/components/RejectedLeaveModal";

// interface TeacherLeavesSectionProps {

//     allowActions?: boolean;
// }

const TeacherLeaves: React.FC = () => {
    const { t } = useTranslation();
    const { teacher } = useOutletContext<any>();

    // leaves hooks
    const {
        statusLeaveOptions,
        handleUpdateStatus,
    } = useLeave();

    // reference data (leave types)
    const { leaveTypes, loadingLeaveTypes, fetchLeaveTypes } = useReference();
    const { leaves, fetchTeacherLeaves, loading, infoPagination, handleUpdateStatusLeaveTeacher } = useTeacher();
    const [isModalRejectedOpen, setIsModalRejectedOpen] = useState<boolean>(false);
    const [selectedLeave, setSelectedLeave] = useState<any>(null);
    const [filters, setFilters] = useState<Record<string, any>>({});
    const staffId = useMemo(() => {
        if (!teacher) {
            return null;
        }
        return teacher?.staff?.id;
    }, [teacher]);
    const leaveTypeOptions = useMemo(
        () => (leaveTypes || []).map((lt) => ({ label: lt.name, value: lt.id })),
        [leaveTypes]
    );


    const filterFields: FilterField[] = useMemo(
        () => [
            { key: "status", label: t("leave.form.status"), type: "select", options: statusLeaveOptions },
            { key: "leaveTypeId", label: t("leave.form.leaveType"), type: "select", options: leaveTypeOptions },
        ],
        [t, statusLeaveOptions, leaveTypeOptions, loadingLeaveTypes]
    );

    const doFetch = useCallback((params: Record<string, any>) => {
        if (!staffId) return;
        fetchTeacherLeaves({ staffId, ...params });
    }, [fetchTeacherLeaves]);

    const handleFilter = (newFilters: Record<string, any>) => {
        const { dateRange, ...rest } = newFilters || {};
        const dateFrom = Array.isArray(dateRange) && dateRange[0] ? dateRange[0] : null;
        const dateTo = Array.isArray(dateRange) && dateRange[1] ? dateRange[1] : null;
        const merged = { ...rest, dateFrom, dateTo };
        setFilters(merged);
        doFetch({ ...merged, page: 1, limit: 10 });
    };

    useEffect(() => {
        fetchLeaveTypes();
        doFetch({ page: 1, limit: 10 });
    }, [staffId]);
    const openRejectedModel = (record: any) => {
        setSelectedLeave(record)
        setIsModalRejectedOpen(true)
    }

    const handleRejected = async (payload: any) => {
        try {
            const result = await handleUpdateStatusLeaveTeacher(selectedLeave?.id, payload);
            if (result) {
                setIsModalRejectedOpen(false);
                setSelectedLeave(null);
                doFetch({ page: 1, limit: 10 });
            }
        } catch (error) {
            console.error("Error fetching leaves:", error);
        }
    }

    const handleApproved = async (id: any) => {
        try {
            const payload = {
                status: 'approved' as any
            }
            const result = await handleUpdateStatusLeaveTeacher(id, payload);
            if (result) {
                doFetch({ page: 1, limit: 10 });
            }
        } catch (error) {
            console.error("Error fetching leaves:", error);
        }
    }

    if (!staffId) return <CustomAlert
        message={t("alert.no_staff_alert.message")}
        description={t("alert.no_staff_alert.description")}
        type="info"
        showIcon
    />;

    if (loading) return <Loader />;

    return (
        <div className="p-2">
            <div className="d-flex mb-3 justify-content-end gap-2">
                <TableFilterDynamique
                    fields={filterFields}
                    filters={filters}
                    onApply={handleFilter}
                    onReset={() => handleFilter({ page: 1, limit: 10 })}
                />
            </div>

            <LeaveTable
                dataSource={leaves}
                loading={loading}
                dataPaginate={{
                    current: infoPagination?.page,
                    pageSize: infoPagination?.limit,
                    total: infoPagination?.total,
                }}
                onChange={(pg: any) => {
                    doFetch({
                        ...filters,
                        page: pg.current!,
                        limit: pg.pageSize!,
                    });
                }}
                onRejected={openRejectedModel}
                onApprove={(record: any) => {
                    handleApproved(record.id)
                }}
            />

            <RejectedLeaveModel
                isOpen={isModalRejectedOpen}
                onClose={() => setIsModalRejectedOpen(false)}
                leaveData={selectedLeave}
                onSave={(value) => handleRejected(value)}
            />
        </div>
    );
};

export default TeacherLeaves;
